import { useState, useEffect, useRef } from 'react';
import {
  Play,
  Plus,
  Trash2,
  Edit2,
  ChevronLeft,
  GripVertical,
  X,
  Check,
  Music,
  ListMusic,
  Save,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAudio } from '@/context/AudioContext';
import type { Playlist, PlaylistTrack, AudioTrack } from '@/types/audio';
import {
  subscribeToPlaylists,
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  reorderPlaylistTracks,
  removeTrackFromPlaylist,
} from '@/services/playlists';
import { NowPlayingIndicator } from './NowPlayingIndicator';

interface PlaylistManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveCurrentQueue?: boolean;
  initialQueue?: AudioTrack[];
}

type View = 'list' | 'detail' | 'create';

/**
 * Playlist manager component for viewing, creating, editing, and playing saved playlists
 */
export function PlaylistManager({
  isOpen,
  onClose,
  onSaveCurrentQueue,
  initialQueue,
}: PlaylistManagerProps) {
  const { authState } = useAuth();
  const { playPlaylist, queue, activePlaylistId, currentTrack, isPlaying } = useAudio();

  // Get user ID from auth state
  const userId = authState.status === 'authenticated' ? authState.profile.uid : null;

  const [view, setView] = useState<View>('list');
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Drag state for track reordering
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Load playlists
  useEffect(() => {
    if (!userId || !isOpen) return;

    const unsubscribe = subscribeToPlaylists(userId, setPlaylists);
    return unsubscribe;
  }, [userId, isOpen]);

  // Handle save current queue trigger
  useEffect(() => {
    if (onSaveCurrentQueue && isOpen) {
      setView('create');
      setNewPlaylistName('');
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [onSaveCurrentQueue, isOpen]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setView('list');
      setSelectedPlaylist(null);
      setNewPlaylistName('');
      setEditingName(false);
      setDeleteConfirm(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreatePlaylist = async () => {
    if (!userId || !newPlaylistName.trim()) return;

    setIsLoading(true);
    try {
      const tracksToSave = initialQueue || queue;
      await createPlaylist(userId, {
        name: newPlaylistName.trim(),
        tracks: tracksToSave,
      });
      setNewPlaylistName('');
      setView('list');
    } catch (error) {
      console.error('Error creating playlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePlaylist = async (playlistId: string) => {
    if (!userId) return;

    try {
      await deletePlaylist(userId, playlistId);
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(null);
        setView('list');
      }
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting playlist:', error);
    }
  };

  const handleRenamePlaylist = async () => {
    if (!userId || !selectedPlaylist || !editedName.trim()) return;

    try {
      await updatePlaylist(userId, selectedPlaylist.id, {
        name: editedName.trim(),
      });
      setEditingName(false);
    } catch (error) {
      console.error('Error renaming playlist:', error);
    }
  };

  const handleRemoveTrack = async (trackIndex: number) => {
    if (!userId || !selectedPlaylist) return;

    try {
      await removeTrackFromPlaylist(userId, selectedPlaylist.id, trackIndex);
    } catch (error) {
      console.error('Error removing track:', error);
    }
  };

  const handleReorderTrack = async (fromIndex: number, toIndex: number) => {
    if (!userId || !selectedPlaylist) return;

    try {
      await reorderPlaylistTracks(userId, selectedPlaylist.id, fromIndex, toIndex);
    } catch (error) {
      console.error('Error reordering tracks:', error);
    }
  };

  const handlePlayPlaylist = (playlist: Playlist, startIndex = 0) => {
    playPlaylist(playlist, startIndex);
    onClose();
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== toIndex) {
      handleReorderTrack(draggedIndex, toIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const renderListView = () => (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">My Playlists</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setView('create');
              setTimeout(() => nameInputRef.current?.focus(), 100);
            }}
            className="p-1.5 text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
            title="New Playlist"
          >
            <Plus size={18} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Playlist list */}
      <div className="flex-1 overflow-y-auto">
        {playlists.length === 0 ? (
          <div className="py-8 text-center">
            <ListMusic size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No playlists yet</p>
            <button
              onClick={() => {
                setView('create');
                setTimeout(() => nameInputRef.current?.focus(), 100);
              }}
              className="mt-3 text-sm text-[#8e4585] hover:underline"
            >
              Create your first playlist
            </button>
          </div>
        ) : (
          <div className="py-2">
            {playlists.map((playlist) => (
              <div
                key={playlist.id}
                className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer ${
                  activePlaylistId === playlist.id ? 'bg-[#f8e8f8]' : ''
                }`}
                onClick={() => {
                  setSelectedPlaylist(playlist);
                  setView('detail');
                }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8e4585] to-[#63345e] flex items-center justify-center text-white font-bold shadow-sm">
                  {playlist.trackCount}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{playlist.name}</p>
                  <p className="text-xs text-slate-500">
                    {playlist.trackCount} track{playlist.trackCount !== 1 ? 's' : ''}
                  </p>
                </div>
                {activePlaylistId === playlist.id && (
                  <NowPlayingIndicator size="sm" paused={!isPlaying} />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayPlaylist(playlist);
                  }}
                  className="p-2 text-[#8e4585] hover:bg-[#f8e8f8] rounded-full transition-colors"
                >
                  <Play size={18} className="ml-0.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const renderCreateView = () => (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <button
          onClick={() => setView('list')}
          className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <h3 className="text-base font-semibold text-slate-800">New Playlist</h3>
      </div>

      {/* Create form */}
      <div className="p-4">
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Playlist Name</label>
          <input
            ref={nameInputRef}
            type="text"
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            placeholder="Enter playlist name"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8e4585]/20 focus:border-[#8e4585]"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreatePlaylist();
            }}
          />
        </div>

        {(initialQueue || queue).length > 0 && (
          <div className="mb-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <span className="font-medium">{(initialQueue || queue).length}</span> tracks will be
              added from {initialQueue ? 'selection' : 'current queue'}
            </p>
          </div>
        )}

        <button
          onClick={handleCreatePlaylist}
          disabled={!newPlaylistName.trim() || isLoading}
          className="w-full py-2.5 bg-gradient-to-r from-[#8e4585] to-[#63345e] text-white rounded-lg font-medium hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Save size={16} />
          Create Playlist
        </button>
      </div>
    </>
  );

  const renderDetailView = () => {
    if (!selectedPlaylist) return null;

    const isPlayingFromThis = activePlaylistId === selectedPlaylist.id;

    return (
      <>
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <button
            onClick={() => {
              setView('list');
              setEditingName(false);
            }}
            className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>

          {editingName ? (
            <div className="flex-1 flex items-center gap-2">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="flex-1 px-2 py-1 border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-[#8e4585]/20"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenamePlaylist();
                  if (e.key === 'Escape') setEditingName(false);
                }}
              />
              <button
                onClick={handleRenamePlaylist}
                className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg"
              >
                <Check size={16} />
              </button>
              <button
                onClick={() => setEditingName(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <h3 className="flex-1 text-base font-semibold text-slate-800 truncate">
                {selectedPlaylist.name}
              </h3>
              <button
                onClick={() => {
                  setEditedName(selectedPlaylist.name);
                  setEditingName(true);
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                title="Rename"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => setDeleteConfirm(selectedPlaylist.id)}
                className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>

        {/* Play button */}
        <div className="px-4 py-3 border-b border-slate-100">
          <button
            onClick={() => handlePlayPlaylist(selectedPlaylist)}
            disabled={selectedPlaylist.tracks.length === 0}
            className="w-full py-2.5 bg-gradient-to-r from-[#8e4585] to-[#63345e] text-white rounded-lg font-medium hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Play size={18} className="ml-0.5" />
            {isPlayingFromThis ? 'Now Playing' : 'Play All'}
          </button>
        </div>

        {/* Track list */}
        <div className="flex-1 overflow-y-auto">
          {selectedPlaylist.tracks.length === 0 ? (
            <div className="py-8 text-center">
              <Music size={24} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No tracks in this playlist</p>
            </div>
          ) : (
            <div className="py-2">
              {selectedPlaylist.tracks.map((track: PlaylistTrack, index: number) => {
                const isCurrentTrack = isPlayingFromThis && currentTrack?.id === track.trackId;
                const isDragging = index === draggedIndex;
                const isDragOver = index === dragOverIndex;

                return (
                  <div
                    key={track.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={() => setDragOverIndex(null)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-2 px-4 py-2 cursor-grab active:cursor-grabbing
                      ${isCurrentTrack ? 'bg-[#f8e8f8]' : 'hover:bg-slate-50'}
                      ${isDragging ? 'opacity-50' : ''}
                      ${isDragOver ? 'border-t-2 border-[#8e4585]' : ''}
                    `}
                  >
                    <GripVertical size={14} className="text-slate-300 flex-shrink-0" />

                    <div className="w-6 flex-shrink-0 flex justify-center">
                      {isCurrentTrack ? (
                        <NowPlayingIndicator size="sm" paused={!isPlaying} />
                      ) : (
                        <span className="text-xs text-slate-400">{index + 1}</span>
                      )}
                    </div>

                    <button
                      onClick={() => handlePlayPlaylist(selectedPlaylist, index)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p
                        className={`text-sm truncate ${
                          isCurrentTrack ? 'text-[#8e4585] font-medium' : 'text-slate-700'
                        }`}
                      >
                        {track.title}
                      </p>
                    </button>

                    <button
                      onClick={() => handleRemoveTrack(index)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Delete confirmation */}
        {deleteConfirm === selectedPlaylist.id && (
          <div className="absolute inset-0 bg-white/95 flex items-center justify-center p-4">
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-4">
                Delete <span className="font-medium">{selectedPlaylist.name}</span>?
              </p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeletePlaylist(selectedPlaylist.id)}
                  className="px-4 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col h-full relative">
      {view === 'list' && renderListView()}
      {view === 'create' && renderCreateView()}
      {view === 'detail' && renderDetailView()}
    </div>
  );
}

export default PlaylistManager;
